import { useContext, useEffect, useState } from 'react';
import { ApiServiceContext } from '../../../../api/ApiService.context';
import { QuoteExperienceService } from '../../../../api/quote-experience/QuoteExperienceService.class';
import { blobToDataURI } from '../../QuoteExperience.helpers';
import { QEImage } from '../../QuoteExperienceQuickSetup.page';
import { BaseImagePath } from '../ImageManager/ImageManager.panel';

type LoadState = 'NOT STARTED' | 'LOADING' | 'DONE';

export type BundleImagePreviews = {
    availableImages: string[];
    imagePreviewMap: Record<string, string>;
};

const fileName = (fullPath: string) => fullPath.split('/').pop() || fullPath;

export const useBundleImagePreviews = (
    bundleGuid: string | undefined,
    baseImagePath: BaseImagePath,
    pendingImages: QEImage[] | undefined
): BundleImagePreviews => {
    const { getApiServiceCollections } = useContext(ApiServiceContext);
    const { QuoteExperienceService } = getApiServiceCollections() as { QuoteExperienceService: QuoteExperienceService };

    const [availableImages, setAvailableImages] = useState<string[]>([]);
    const [imagePreviewMap, setImagePreviewMap] = useState<Record<string, string>>({});
    const [loadState, setLoadState] = useState<LoadState>('NOT STARTED');

    useEffect(() => {
        if (loadState !== 'NOT STARTED') return;
        setLoadState('LOADING');

        const loadBundleImages = async (names: string[], previews: Record<string, string>) => {
            if (!bundleGuid) return;
            const resp = await QuoteExperienceService.listAssetFiles({ bundleGuid, folderPath: `assets/${baseImagePath}` });
            names.push(...(resp.jsonBody ?? []).map(fileName));
            await Promise.all(names.map(async (name) => {
                try {
                    const blob = await QuoteExperienceService.loadImage(bundleGuid, `assets/${baseImagePath}/${name}`);
                    const dataUri = blob ? await blobToDataURI(blob) : null;
                    if (dataUri) previews[name] = dataUri as string;
                } catch { /* skip failed previews */ }
            }));
        };

        const loadPendingImages = async (names: string[], previews: Record<string, string>) => {
            await Promise.all((pendingImages || []).map(async (pending) => {
                try {
                    const previewUrl = pending.previewUrl || (pending.file ? await blobToDataURI(pending.file) : null);
                    if (!previewUrl) return;
                    names.push(pending.name);
                    previews[pending.name] = previewUrl as string;
                } catch { /* skip failed previews */ }
            }));
        };

        const load = async () => {
            const names: string[] = [];
            const previews: Record<string, string> = {};
            try {
                await loadBundleImages(names, previews);
                await loadPendingImages(names, previews);
                setAvailableImages(names);
                setImagePreviewMap(previews);
            } catch (err) {
                console.error(`Failed to load available ${baseImagePath}`, err);
            } finally {
                setLoadState('DONE');
            }
        };

        load();
    }, [loadState, baseImagePath, bundleGuid, pendingImages, QuoteExperienceService]);

    return { availableImages, imagePreviewMap };
};
